import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play, Save, Download, FolderOpen, Trash2 } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney } from "@/lib/format";
import { moneyTick, CATEGORY_COLORS } from "@/lib/charts";
import { PageHeader } from "@/components/common/PageHeader";
import { PeriodFilter } from "@/components/common/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const DIMS = ["company", "property", "sub", "service_type", "brand", "vendedor", "mes", "semana"];
const MEASURES = ["facturacion", "costo", "margen_valor", "margen_pct", "cantidad_ordenes",
  "tiempo_promedio_cierre", "monto_pendiente_facturar", "monto_pendiente_cobrar"];
const MONEY_KEYS = new Set(["facturacion_cents", "costo_cents", "margen_valor_cents",
  "monto_pendiente_facturar_cents", "monto_pendiente_cobrar_cents"]);

function formatCell(t, key, value) {
  if (MONEY_KEYS.has(key)) return formatMoney(value);
  if (key.endsWith("_pct")) return value === null || value === undefined ? t("wo.margin_na") : `${value}%`;
  if (key.endsWith("_horas")) return value === null || value === undefined ? "—" : `${value} h`;   // Fix T
  return value;
}

export default function ReportBuilderPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [dimension, setDimension] = useState("company");
  const [dimension2, setDimension2] = useState("none");
  const [medidas, setMedidas] = useState(["facturacion", "costo", "margen_valor", "cantidad_ordenes"]);
  const [period, setPeriod] = useState({ preset: "this_month" });
  const [result, setResult] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [nombre, setNombre] = useState("");

  const config = { dimension, dimension2: dimension2 === "none" ? null : dimension2, medidas, desde: period.desde, hasta: period.hasta };

  const run = useMutation({
    mutationFn: () => api.post("/reports/run", config),
    onSuccess: ({ data }) => setResult(data),
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const { data: saved } = useQuery({ queryKey: ["saved-reports"], queryFn: async () => (await api.get("/reports")).data.items });
  const save = useMutation({
    mutationFn: () => api.post("/reports", { nombre, config }),
    onSuccess: () => { toast.success("Reporte guardado"); setSaveOpen(false); setNombre(""); qc.invalidateQueries({ queryKey: ["saved-reports"] }); },
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/reports/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-reports"] }),
  });
  const open = (r) => {
    setDimension(r.config.dimension); setDimension2(r.config.dimension2 || "none");
    setMedidas(r.config.medidas); setPeriod({ preset: "custom", desde: r.config.desde, hasta: r.config.hasta });
  };

  const exportFile = async (formato) => {
    const res = await api.post(`/reports/export?formato=${formato}`, config, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `reporte.${formato}`; a.click();
    URL.revokeObjectURL(url);
  };

  const columns = result ? Object.keys(result.totales).filter((k) => k !== "grupo" && k !== "subgrupo") : [];

  return (
    <>
      <PageHeader testId="reports-page-header" title={t("reports.title")} subtitle={t("reports.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="reports-page">
        <div className="mb-4 grid gap-4 rounded-[10px] border border-line bg-white p-4 lg:grid-cols-[1fr_1fr_2fr]">
          <div>
            <p className="mb-1.5 text-[12px] font-medium text-ink-tertiary">{t("reports.dimension")}</p>
            <Select value={dimension} onValueChange={setDimension}>
              <SelectTrigger data-testid="report-dimension-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DIMS.map((d) => <SelectItem key={d} value={d}>{t(`reports.dims.${d}`)}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mb-1.5 mt-3 text-[12px] font-medium text-ink-tertiary">{t("reports.dimension2")}</p>
            <Select value={dimension2} onValueChange={setDimension2}>
              <SelectTrigger data-testid="report-dimension2-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {DIMS.map((d) => <SelectItem key={d} value={d}>{t(`reports.dims.${d}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-1.5 text-[12px] font-medium text-ink-tertiary">{t("reports.measures")}</p>
            <div className="space-y-1.5">
              {MEASURES.map((m) => (
                <label key={m} className="flex items-center gap-2 text-[12.5px]">
                  <Checkbox data-testid={`report-measure-${m}`} checked={medidas.includes(m)}
                    onCheckedChange={(v) => setMedidas((arr) => v ? [...arr, m] : arr.filter((x) => x !== m))} />
                  {t(`reports.measure_names.${m}`)}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="mb-1.5 text-[12px] font-medium text-ink-tertiary">{t("period.label")}</p>
              <PeriodFilter value={period} onChange={setPeriod} testId="report-period-filter" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="report-run-button" className="bg-brand hover:bg-brand-hover" disabled={run.isPending} onClick={() => run.mutate()}>
                <Play className="mr-1.5 h-4 w-4" />{t("reports.run")}
              </Button>
              <Button data-testid="report-save-button" variant="outline" onClick={() => setSaveOpen(true)}>
                <Save className="mr-1.5 h-4 w-4" />{t("reports.save")}
              </Button>
              <Button data-testid="report-export-xlsx-button" variant="outline" onClick={() => exportFile("xlsx")}>
                <Download className="mr-1.5 h-4 w-4" />{t("reports.export_xlsx")}
              </Button>
              <Button data-testid="report-export-pdf-button" variant="outline" onClick={() => exportFile("pdf")}>
                <Download className="mr-1.5 h-4 w-4" />{t("reports.export_pdf")}
              </Button>
            </div>
          </div>
        </div>

        {saved?.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] border border-line bg-white p-3" data-testid="saved-reports-list">
            <FolderOpen className="h-4 w-4 text-ink-tertiary" />
            {saved.map((r) => (
              <span key={r.id} className="flex items-center gap-1 rounded-[6px] border border-line px-2 py-1 text-[12px]">
                <button data-testid={`saved-report-open-${r.id}`} onClick={() => open(r)} className="text-brand">{r.nombre}</button>
                <button data-testid={`saved-report-delete-${r.id}`} onClick={() => remove.mutate(r.id)}><Trash2 className="h-3 w-3 text-ink-tertiary" /></button>
              </span>
            ))}
          </div>
        )}

        {result && (
          <div className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-[10px] border border-line bg-white" data-testid="report-results-table">
              <table className="w-full text-[12.5px]">
                <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
                  <tr>
                    <th className="px-3 py-2 text-left">{t(`reports.dims.${result.dimension}`)}</th>
                    {result.dimension2 && <th className="px-3 py-2 text-left">{t(`reports.dims.${result.dimension2}`)}</th>}
                    {/* Fix T (2026-10-05): título legible, nunca la clave técnica (_CENTS) */}
                    {columns.map((c) => <th key={c} className="px-3 py-2 text-left">{t(`reports.columns.${c}`)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-3 py-1.5">{r.grupo}</td>
                      {result.dimension2 && <td className="px-3 py-1.5">{r.subgrupo}</td>}
                      {columns.map((c) => (
                        <td key={c} className="px-3 py-1.5 tabular-nums">{formatCell(t, c, r[c])}</td>
                      ))}
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="px-3 py-1.5">{t("chart.total")}</td>
                    {result.dimension2 && <td />}
                    {columns.map((c) => (
                      <td key={c} className="px-3 py-1.5 tabular-nums">{formatCell(t, c, result.totales[c])}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="rounded-[10px] border border-line bg-white p-4" data-testid="report-chart">
              <ResponsiveContainer width="100%" height={280}>
                {result.tipo_grafico === "linea" ? (
                  <LineChart data={result.rows}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                    <XAxis dataKey="grupo" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <YAxis tickFormatter={(v) => (MONEY_KEYS.has(columns[0]) ? moneyTick(v) : v)} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <Tooltip />
                    {columns.slice(0, 1).map((c) => <Line isAnimationActive={false} key={c} type="monotone" dataKey={c} stroke={CATEGORY_COLORS[0]} strokeWidth={2} dot={false} />)}
                  </LineChart>
                ) : (
                  <BarChart data={result.rows} layout="vertical" margin={{ left: 90 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                    <YAxis type="category" dataKey="grupo" width={85} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                    <Tooltip />
                    {columns.slice(0, 1).map((c) => <Bar isAnimationActive={false} key={c} dataKey={c} fill={CATEGORY_COLORS[0]} radius={4} barSize={16} />)}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {!result && <p className="text-center text-[13px] text-ink-tertiary">{t("reports.no_results")}</p>}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="report-save-dialog" className="max-w-sm rounded-[10px]">
          <DialogHeader><DialogTitle>{t("reports.save_as")}</DialogTitle></DialogHeader>
          <Input data-testid="report-name-input" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t("reports.name")} />
          <DialogFooter>
            <Button data-testid="report-save-confirm" disabled={!nombre.trim()} onClick={() => save.mutate()}>{t("reports.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
